import { ValidatingAddress } from 'domains/shippingDomain';
import { Form, Space, Popover } from 'antd';
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
} from 'react';
import { RevxMessage } from 'components/RevxMessage';
import { useTranslation } from 'react-i18next';
import { AddressSuggestionsTable } from './AddressSuggestionsTable';
import { UnderlineText } from 'components/RevxTypography';
import { isEqual, mapValues, pick, toUpper } from 'lodash-es';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { isUnitedStates } from 'utils/address';
import { RevxSpin } from 'components/RevxSpin';

type Props = {
  address: ValidatingAddress;
  onApply: (record: ValidatingAddress) => void;
};
export type ValidationRef = {
  validate: (values: ValidatingAddress) => Promise<boolean>;
  reset: () => void;
};
const AddressValidation = (
  { address, onApply }: Props,
  ref: React.Ref<ValidationRef>
) => {
  const [validatingAddress, setValidatingAddress] =
    useState<ValidatingAddress>(address);
  const [validateError, setValidateError] =
    useState<ValidatingAddress[] | string>();
  const validating = useAppSelector(
    (state) => state.loading.effects.shipping.validateAddress
  );
  const errorRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (validateError) {
      errorRef.current?.scrollIntoView();
    }
  }, [validateError]);

  useImperativeHandle(ref, () => {
    return {
      validate: async (address: ValidatingAddress) => {
        setValidatingAddress(address);

        // UPS address validation only covers US addresses. Running it anywhere else just rejects
        // perfectly good international addresses - not least ones with no state/province, which
        // many countries do not have (PRD-746).
        if (!isUnitedStates(address.countryCode)) {
          setValidateError('');
          return true;
        }

        const addrObj = mapValues(
          pick(address, [
            'addressLine',
            'city',
            'countryCode',
            'postalCode',
            'stateProvinceCode',
          ]),
          (value, key) => {
            return toUpper(value);
          }
        );

        try {
          const data = await dispatch.shipping.validateAddress(addrObj);
          if (
            data.length !== 1 ||
            !isEqual(
              addrObj,
              pick(data[0], [
                'addressLine',
                'city',
                'countryCode',
                'postalCode',
                'stateProvinceCode',
              ])
            )
          ) {
            setValidateError(data);
            return false;
          } else {
            return true;
          }
        } catch (e: any) {
          setValidateError(e.message);
          return false;
        }
      },
      reset() {
        setValidateError('');
      },
    };
  });

  return (
    <RevxSpin
      spinning={validating}
      size="large"
      tip={t('platform.shipping.validate.addressValidating')}
    >
      {validateError ? (
        <div ref={errorRef}>
          <Form.Item wrapperCol={{ span: 24 }}>
            <RevxMessage
              closable={false}
              type="error"
              message={
                <Space>
                  {Array.isArray(validateError) && (
                    <Space>
                      {t('platform.shipping.validate.notValidText')}
                      {'  '}
                      <Popover
                        placement="bottom"
                        content={
                          <AddressSuggestionsTable
                            suggestions={validateError}
                            address={validatingAddress}
                            onApply={(record) => {
                              setValidateError('');
                              onApply(record);
                            }}
                          />
                        }
                      >
                        <UnderlineText
                          color="blue"
                          style={{ display: 'inline-block', marginBottom: 0 }}
                        >
                          {t('platform.shipping.validate.suggestions')}
                        </UnderlineText>
                      </Popover>
                    </Space>
                  )}
                  <span>
                    {typeof validateError === 'string' && validateError}
                  </span>
                </Space>
              }
            />
          </Form.Item>
        </div>
      ) : null}
    </RevxSpin>
  );
};

export default forwardRef(AddressValidation);
